import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Award, Video, TrendingUp, Users, Building2, Zap, Gift } from "lucide-react";

const brl = (n) => Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const TIER_COLORS = {
  ouro:   "from-[#F1C232] to-[#D4A017] text-[#3B2F0B]",
  prata:  "from-slate-300 to-slate-500 text-slate-900",
  bronze: "from-[#CD7F32] to-[#8B4513] text-white",
};

const STATUS_LABEL = {
  pending_payment: "Pagamento pendente",
  paid: "Pago (aguardando consulta)",
  completed: "Consulta concluída",
  no_show: "Não compareceu",
  declined_by_professional: "Recusada pelo profissional",
  failed: "Falha na criação",
  cancelled: "Cancelada",
};

const STATUS_COLOR = {
  paid: "text-emerald-700 bg-emerald-50 border-emerald-200",
  completed: "text-slate-700 bg-slate-100 border-slate-200",
  no_show: "text-amber-700 bg-amber-50 border-amber-200",
  declined_by_professional: "text-red-700 bg-red-50 border-red-200",
  pending_payment: "text-yellow-800 bg-yellow-50 border-yellow-200",
  failed: "text-red-700 bg-red-50 border-red-200",
  cancelled: "text-slate-600 bg-slate-50 border-slate-200",
};

/**
 * Sub-aba "Credenciados" do painel Financeiro — mostra:
 *  1) Bookings do marketplace: por status + splits 65/35/10.
 *  2) Receita real de avulsos + combos na janela.
 *  3) Planos ativos por selo (Ouro/Prata/Bronze) × preço mensal (MRR).
 */
const FinanceCredenciadosPanel = ({ days = 90 }) => {
  const [d, setD] = useState(null);
  const [windowDays, setWindowDays] = useState(days);

  const load = () => {
    api.get(`/admin/finance/credenciados?days=${windowDays}`)
      .then((r) => setD(r.data))
      .catch(() => setD(null));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowDays]);

  if (!d) return <div className="p-6 text-sm text-slate-500">Carregando marketplace…</div>;

  const b = d.bookings.totals;
  const profTiers = d.plans.professionals.tiers;
  const clinTiers = d.plans.clinics.tiers;
  const profPayingTiers = d.plans.professionals.paying_tiers || d.plans.professionals.tiers;
  const clinPayingTiers = d.plans.clinics.paying_tiers || d.plans.clinics.tiers;
  const profCourtesyTiers = d.plans.professionals.courtesy_tiers || { ouro: 0, prata: 0, bronze: 0 };
  const clinCourtesyTiers = d.plans.clinics.courtesy_tiers || { ouro: 0, prata: 0, bronze: 0 };
  const profOneshotTiers = d.plans.professionals.oneshot_tiers || { ouro: 0, prata: 0, bronze: 0 };
  const clinOneshotTiers = d.plans.clinics.oneshot_tiers || { ouro: 0, prata: 0, bronze: 0 };
  const profPrices = d.plans.professionals.prices;
  const clinPrices = d.plans.clinics.prices;
  const profOneshotPrices = d.plans.professionals.oneshot_prices || profPrices;
  const clinOneshotPrices = d.plans.clinics.oneshot_prices || clinPrices;
  const courtesyValue = d.plans.courtesy_value_forgone || 0;
  const oneshotActiveValue = d.plans.oneshot_active_value || 0;
  const ot = d.one_time_revenue || { oneshot_total: 0, combo_total: 0, grand_total: 0, oneshot_count: 0, combo_count: 0, recent: [] };

  return (
    <div className="space-y-5" data-testid="finance-credenciados-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-display font-bold text-lg text-[#0B2B62] inline-flex items-center gap-2">
          <Award className="w-5 h-5 text-amber-600" /> Marketplace de credenciados
        </h3>
        <div className="flex gap-1 text-xs">
          {[[30,"30d"],[90,"90d"],[180,"6m"],[365,"1a"]].map(([v,lbl]) => (
            <button key={v} onClick={() => setWindowDays(v)} data-testid={`cred-range-${v}`}
              className={`px-3 py-1.5 rounded-md border ${windowDays === v ? "bg-[#0B2B62] text-white border-[#0B2B62]" : "text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{lbl}</button>
          ))}
        </div>
      </div>

      {/* Splits das consultas */}
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2 inline-flex items-center gap-1.5">
          <Video className="w-3.5 h-3.5" /> Consultas online (bookings)
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card label="Bruto pago" value={brl(b.gross_paid)} accent="emerald" />
          <Card label="65% Profissional" value={brl(b.professional_share)} accent="slate" />
          <Card label="35% RAF (bruto)" value={brl(b.raf_commission)} accent="amber" />
          <Card label="10% Consultor" value={brl(b.consultant_share)} accent="blue" />
          <Card label="RAF líquido" value={brl(b.raf_net_after_consultant)} accent="emerald" />
        </div>
        {/* Iter 68.49 — Passivos e reembolsos (só aparecem se houver movimento) */}
        {(b.refunded > 0 || b.family_credit_liability_period > 0 || (d.family_credits?.available_total || 0) > 0) && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="finance-liabilities">
            {b.refunded > 0 && (
              <Card label="Reembolsos elegíveis (janela)" value={brl(b.refunded)} accent="red"
                sub={`RAF net após refunds: ${brl(b.raf_net_after_refunds)}`} />
            )}
            {b.family_credit_liability_period > 0 && (
              <Card label="Crédito gerado a famílias (janela)" value={brl(b.family_credit_liability_period)} accent="amber"
                sub="Por no-show 50/50" />
            )}
            {(d.family_credits?.available_total || 0) > 0 && (
              <Card label={`Passivo total com famílias (${d.family_credits.available_count})`}
                value={brl(d.family_credits.available_total)} accent="red"
                sub="Créditos disponíveis ainda não usados" />
            )}
          </div>
        )}

        <div className="mt-3 border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[440px]">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-right">Quant.</th>
                <th className="px-4 py-2 text-right">Bruto</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(d.bookings.by_status || []).length === 0 && (
                <tr><td colSpan={3} className="p-6 text-center text-slate-400 text-sm">Sem reservas na janela.</td></tr>
              )}
              {(d.bookings.by_status || []).map((s) => (
                <tr key={s.status} data-testid={`cred-status-${s.status}`}>
                  <td className="px-4 py-2 text-slate-700">{STATUS_LABEL[s.status] || s.status}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-bold">{s.count}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{brl(s.gross)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Iter 68.48 — Últimos bookings detalhados com profissional/família */}
        {(d.bookings.recent || []).length > 0 && (
          <div className="mt-4 border border-slate-200 rounded-xl overflow-hidden" data-testid="finance-recent-bookings">
            <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-widest text-slate-500 font-semibold">
              Últimas {(d.bookings.recent || []).length} consultas · Profissional & Família
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[820px]">
                <thead className="bg-white border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Quando</th>
                    <th className="px-3 py-2 text-left">Profissional</th>
                    <th className="px-3 py-2 text-left">Paciente</th>
                    <th className="px-3 py-2 text-left">Consultor</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-right">Valor</th>
                    <th className="px-3 py-2 text-right">Prof.</th>
                    <th className="px-3 py-2 text-right">RAF</th>
                    <th className="px-3 py-2 text-right">Consultor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(d.bookings.recent || []).map((r) => {
                    const cls = STATUS_COLOR[r.status] || STATUS_COLOR.paid;
                    return (
                      <tr key={r.id} data-testid={`recent-booking-${r.id}`}>
                        <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                          {r.scheduled_at ? new Date(r.scheduled_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                        </td>
                        <td className="px-3 py-2 font-semibold text-[#0B2B62]">{r.professional_name}</td>
                        <td className="px-3 py-2 text-slate-700">{r.family_display}</td>
                        <td className="px-3 py-2 text-blue-800 font-semibold" data-testid={`recent-consultant-${r.id}`}>
                          {r.consultant_name && r.consultant_name !== "—" ? (
                            r.consultant_name
                          ) : (
                            <button onClick={() => {
                              const email = window.prompt("E-mail do consultor a atribuir a esta reserva:");
                              if (!email) return;
                              api.post(`/admin/bookings/${r.id}/set-consultant`, { consultant_email: email })
                                .then(() => { toast.success("Consultor atribuído!"); load(); })
                                .catch(e => toast.error(e?.response?.data?.detail || "Erro"));
                            }} data-testid={`assign-consultant-${r.id}`}
                              className="text-[10px] px-2 py-0.5 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 font-semibold">
                              atribuir
                            </button>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${cls}`}>
                            {STATUS_LABEL[r.status] || r.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{brl(r.value)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{brl(r.professional_share)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-amber-700">{brl(r.raf_commission)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-blue-700">{brl(r.consultant_share)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Receita REAL — avulsos + combos na janela */}
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2 inline-flex items-center gap-1.5">
          <Zap className="w-3.5 h-3.5" /> Receita de credenciamentos (dinheiro que já entrou)
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Card label={`Avulsos (${ot.oneshot_count})`} value={brl(ot.oneshot_total)} accent="emerald" />
          <Card label={`Combos 3+1 (${ot.combo_count})`} value={brl(ot.combo_total)} accent="blue" />
          <Card label="Total da janela" value={brl(ot.grand_total)} accent="amber" />
        </div>
        {(ot.recent || []).length > 0 && (
          <div className="mt-3 border border-slate-200 rounded-xl overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]" data-testid="cred-oneshot-recent">
              <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left">Quando</th>
                  <th className="px-4 py-2 text-left">Quem</th>
                  <th className="px-4 py-2 text-left">Tipo</th>
                  <th className="px-4 py-2 text-left">Selo</th>
                  <th className="px-4 py-2 text-right">Valor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {ot.recent.map((r, i) => (
                  <tr key={i} data-testid={`cred-charge-${i}`}>
                    <td className="px-4 py-2 text-slate-500 tabular-nums text-xs">{(r.granted_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-2 text-slate-800">
                      <span className="inline-flex items-center gap-1.5">
                        {r.kind === "prof" ? <Users className="w-3.5 h-3.5 text-slate-400" /> : <Building2 className="w-3.5 h-3.5 text-slate-400" />}
                        {r.name}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-slate-600">
                      {r.type === "combo" ? (
                        <span className="inline-flex items-center gap-1 text-blue-700 font-semibold text-xs"><Gift className="w-3 h-3" /> Combo 3+1</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold text-xs"><Zap className="w-3 h-3" /> Avulso 30d</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-widest bg-gradient-to-r ${TIER_COLORS[r.tier] || "from-slate-200 to-slate-300 text-slate-700"}`}>{r.tier || "—"}</span>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums font-bold text-emerald-700">{brl(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Planos ativos */}
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2 inline-flex items-center gap-1.5">
          <TrendingUp className="w-3.5 h-3.5" /> Planos de certificação (MRR real — só pagantes)
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card label="Receita recorrente estimada / mês" value={brl(d.plans.monthly_recurring_revenue)} accent="emerald" />
          <Card label="Cortesias concedidas / mês" value={brl(courtesyValue)} accent="slate" />
          <Card label="Avulsos vigentes agora" value={brl(oneshotActiveValue)} accent="amber" />
        </div>

        <div className="grid md:grid-cols-2 gap-3 mt-3">
          <TiersCard title="Profissionais (pagantes)" icon={Users} tiers={profPayingTiers} prices={profPrices} testId="prof-tiers-paying" />
          <TiersCard title="Clínicas (pagantes)" icon={Building2} tiers={clinPayingTiers} prices={clinPrices} testId="clin-tiers-paying" />
        </div>

        {(Object.values(profOneshotTiers).some(v => v > 0) || Object.values(clinOneshotTiers).some(v => v > 0)) && (
          <>
            <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mt-4 mb-2 inline-flex items-center gap-1.5">
              <Zap className="w-3 h-3" /> Avulsos vigentes (pagos com +10%, expira em 30d)
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <TiersCard title="Profissionais (avulsos)" icon={Users} tiers={profOneshotTiers} prices={profOneshotPrices} testId="prof-tiers-oneshot" />
              <TiersCard title="Clínicas (avulsos)" icon={Building2} tiers={clinOneshotTiers} prices={clinOneshotPrices} testId="clin-tiers-oneshot" />
            </div>
          </>
        )}

        {(Object.values(profCourtesyTiers).some(v => v > 0) || Object.values(clinCourtesyTiers).some(v => v > 0)) && (
          <>
            <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mt-4 mb-2">
              Cortesias vigentes (não somam ao MRR — apenas visibilidade)
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <TiersCard title="Profissionais (cortesias)" icon={Users} tiers={profCourtesyTiers} prices={profPrices} testId="prof-tiers-courtesy" />
              <TiersCard title="Clínicas (cortesias)" icon={Building2} tiers={clinCourtesyTiers} prices={clinPrices} testId="clin-tiers-courtesy" />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const TiersCard = ({ title, icon: Icon, tiers, prices, testId }) => {
  const total = Object.keys(tiers).reduce((s, t) => s + (tiers[t] || 0) * (prices[t] || 0), 0);
  return (
    <div className="border border-slate-200 rounded-xl p-4 bg-white" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-slate-500" />
        <div className="font-semibold text-[#0B2B62]">{title}</div>
      </div>
      <div className="space-y-1.5">
        {["ouro", "prata", "bronze"].map((t) => (
          <div key={t} className="flex items-center justify-between text-sm">
            <span className={`inline-flex items-center gap-1.5 text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-widest bg-gradient-to-r ${TIER_COLORS[t]}`}>
              <Award className="w-3 h-3" /> {t}
            </span>
            <span className="tabular-nums text-slate-700">
              <b>{tiers[t] || 0}</b> × {brl(prices[t])} = <b className="text-[#0B2B62]">{brl((tiers[t] || 0) * (prices[t] || 0))}</b>
            </span>
          </div>
        ))}
      </div>
      <div className="mt-3 pt-2 border-t border-slate-100 flex justify-between text-sm">
        <b>Subtotal</b>
        <b className="text-emerald-700 tabular-nums">{brl(total)}</b>
      </div>
    </div>
  );
};

const Card = ({ label, value, accent, wide }) => {
  const map = {
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-800",
    blue: "border-blue-300 bg-blue-50 text-blue-800",
    amber: "border-amber-300 bg-amber-50 text-amber-800",
    slate: "border-slate-300 bg-slate-50 text-slate-800",
  };
  return (
    <div className={`p-3 border rounded-lg ${map[accent] || "border-slate-200 bg-white"} ${wide ? "col-span-full" : ""}`}>
      <div className="text-[10px] uppercase tracking-widest font-semibold opacity-80">{label}</div>
      <div className="font-display font-bold text-2xl mt-1 tabular-nums">{value}</div>
    </div>
  );
};

export default FinanceCredenciadosPanel;
